import React, { useState, useEffect } from 'react';
import VersionBadge from '../VersionBadge/VersionBadge';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Table,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import { useOrganizationStore } from '../../store/organization.store';

interface Review {
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'recalled';
  reviewCode: string | null;
  clientOrgId: string | null;
  clientOrgName: string | null;
  sourceOrgName: string;
  notes: string;
  createdAt: number;
  createdBy?: string;
  createdByName?: string;
  createdByEmail?: string;
  sharedAt: number;
  reviewedAt?: number;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedByEmail?: string;
  reviewedVia?: 'in-app' | 'external-link';
  rejectionReason?: string;
  recalledAt?: number;
  recalledBy?: string;
  expiresAt?: number;
  fileVersion?: number;
}

interface ReviewHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackName: string;
  trackId: string;
  firebase: any;
  onShareForReview?: () => void;
  sourceOrgId?: string; // For client view: query the source org's reviews instead of current org
  onRecall?: () => void; // Recall a pending review (artist only)
  onUpdate?: () => void; // Navigate to edit page for rejected tracks (artist only)
}

const ReviewHistoryModal: React.FC<ReviewHistoryModalProps> = ({
  isOpen,
  onClose,
  trackName,
  trackId,
  firebase,
  onShareForReview,
  sourceOrgId,
  onRecall,
  onUpdate,
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { currentOrgId } = useOrganizationStore();

  // Load reviews when modal opens
  useEffect(() => {
    const loadReviews = async () => {
      if (!isOpen || !firebase) return;
      // Use sourceOrgId for client view (reviews live on source org's track)
      const orgIdForQuery = sourceOrgId || currentOrgId;
      if (!orgIdForQuery) return;

      setLoading(true);
      try {
        const fetchedReviews = await firebase.getReviewsForTrack(orgIdForQuery, trackId);
        setReviews(fetchedReviews);
      } catch (error) {
        console.error('Error loading reviews:', error);
      }
      setLoading(false);
    };

    loadReviews();
  }, [isOpen, currentOrgId, sourceOrgId, trackId, firebase]);

  const copyLink = (reviewCode: string, reviewId: string) => {
    const link = `${window.location.origin}/review/${reviewCode}`;
    navigator.clipboard.writeText(link);
    setCopiedId(reviewId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getStatusBadge = (review: Review) => {
    const statusMap: Record<string, { color: string; label: string }> = {
      approved: { color: '#50c878', label: 'Approved' },
      rejected: { color: 'var(--button-danger)', label: 'Rejected' },
      pending: { color: 'var(--button-warning)', label: 'Pending' },
      recalled: { color: 'rgba(150, 150, 150, 0.8)', label: 'Recalled' },
    };
    const s = statusMap[review.status] || { color: 'rgba(255,255,255,0.5)', label: review.status };
    return (
      <span className="version-badge" style={{
        padding: '1px 5px', borderRadius: '3px', fontSize: '0.6rem', fontWeight: 600,
        backgroundColor: `color-mix(in srgb, ${s.color} 20%, transparent)`,
        whiteSpace: 'nowrap',
        ['--badge-color' as any]: s.color,
      } as React.CSSProperties}>
        {s.label}
      </span>
    );
  };

  const getSharedWith = (review: Review) => {
    if (review.clientOrgName) return review.clientOrgName;
    if (review.clientOrgId) return 'Organization';
    if (review.reviewCode) return 'Link only';
    return '--';
  };


  const isLinkExpired = (review: Review) => {
    return review.expiresAt ? review.expiresAt < Date.now() : false;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      style={{
        color: '#ffffff',
        maxWidth: '900px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={onClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={onClose}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>Review History - {trackName}</span>
      </ModalHeader>
      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '500px',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner style={{ color: 'var(--accent-1-color)' }} size="sm" />
            <small className="text-muted d-block mt-2">Loading review history...</small>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted mb-2">No review history for this spot.</p>
            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
              Click "Share for Review" below to get started.
            </p>
          </div>
        ) : (
          reviews.map((review, reviewIdx) => {
            const isExpanded = expandedId === review.id;
            // Only show Update on the most recent (first) rejected review
            const isLatestRejected = review.status === 'rejected' && !reviews.slice(0, reviewIdx).some(r => r.status === 'rejected');
            return (
              <div
                key={review.id}
                style={{
                  marginBottom: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  overflow: 'hidden',
                }}
              >
                {/* Summary row — always visible, clickable to expand */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s',
                  }}
                  onClick={() => setExpandedId(isExpanded ? null : review.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.75rem', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                      &#9654;
                    </span>
                    {getStatusBadge(review)}
                    {review.fileVersion && (
                      <VersionBadge type="version" version={review.fileVersion} status={review.status} />
                    )}
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}>
                      {moment(review.createdAt).format('DD/MM/YYYY HH:mm')}
                    </span>
                    <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '0.75rem' }}>
                      — {getSharedWith(review)}
                    </span>
                  </div>
                  {/* Action buttons for pending reviews (always visible, don't need to expand) */}
                  {review.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      {review.reviewCode && !isLinkExpired(review) && (
                        <Button
                          size="sm"
                          className={copiedId === review.id ? 'btn-success-custom' : 'btn-primary-custom'}
                          onClick={() => copyLink(review.reviewCode!, review.id)}
                          style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                        >
                          <span style={{ position: 'relative', zIndex: 2, color: '#fff' }}>
                            {copiedId === review.id ? 'Copied!' : 'Copy Link'}
                          </span>
                        </Button>
                      )}
                      {review.reviewCode && isLinkExpired(review) && (
                        <span style={{ color: 'rgba(255, 80, 80, 0.7)', fontSize: '0.7rem' }}>Expired</span>
                      )}
                      {onRecall && (
                        <Button
                          size="sm"
                          className="btn-danger-custom"
                          onClick={onRecall}
                          style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                        >
                          <span style={{ position: 'relative', zIndex: 2 }}>Recall</span>
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Update button for the most recent rejected review only (artist only) */}
                  {isLatestRejected && onUpdate && (
                    <Button
                      size="sm"
                      className="btn-primary-custom"
                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); onUpdate(); }}
                      style={{ fontSize: '0.7rem', padding: '2px 6px' }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>Update</span>
                    </Button>
                  )}
                </div>

                {/* Expanded detail — full lifecycle info */}
                {isExpanded && (
                  <div style={{
                    padding: '0 1rem 1rem 1rem',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                  }}>
                    {/* Sharing details */}
                    <div style={{
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '6px',
                      marginBottom: '0.5rem',
                    }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Shared
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem', fontSize: '0.85rem' }}>
                        <div>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>By: </span>
                          <span style={{ color: '#fff' }}>{review.createdByName || review.createdByEmail || '--'}</span>
                        </div>
                        {(review as any).sharedByOrgName && (
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Org: </span>
                            <span style={{ color: '#fff' }}>{(review as any).sharedByOrgName}</span>
                          </div>
                        )}
                        <div>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>To: </span>
                          <span style={{ color: '#fff' }}>{getSharedWith(review)}</span>
                        </div>
                        <div>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Method: </span>
                          <span style={{ color: '#fff' }}>
                            {review.clientOrgId ? 'In-app' : 'Link'}
                            {review.clientOrgId && review.reviewCode ? ' + Link' : ''}
                          </span>
                        </div>
                        <div>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Date: </span>
                          <span style={{ color: '#fff' }}>{moment(review.createdAt).format('DD/MM/YYYY HH:mm')}</span>
                        </div>
                        {review.fileVersion && (
                          <div>
                            <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>File version: </span>
                            <VersionBadge type="version" version={review.fileVersion} status={review.status} />
                          </div>
                        )}
                      </div>
                      {review.notes && (
                        <div style={{ marginTop: '0.375rem' }}>
                          <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.85rem' }}>Notes: </span>
                          <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>{review.notes}</span>
                        </div>
                      )}
                    </div>

                    {/* Review outcome (for non-pending) */}
                    {review.status !== 'pending' && (
                      <div style={{
                        padding: '0.75rem',
                        backgroundColor: review.status === 'approved'
                          ? 'rgba(80, 200, 120, 0.05)'
                          : review.status === 'rejected'
                            ? 'rgba(255, 80, 80, 0.05)'
                            : 'rgba(150, 150, 150, 0.05)',
                        borderRadius: '6px',
                        border: `1px solid ${
                          review.status === 'approved'
                            ? 'rgba(80, 200, 120, 0.15)'
                            : review.status === 'rejected'
                              ? 'rgba(255, 80, 80, 0.15)'
                              : 'rgba(150, 150, 150, 0.15)'
                        }`,
                      }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {review.status === 'recalled' ? 'Recalled' : 'Reviewed'}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem', fontSize: '0.85rem' }}>
                          {review.status === 'recalled' ? (
                            <>
                              <div>
                                <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Date: </span>
                                <span style={{ color: '#fff' }}>
                                  {review.recalledAt ? moment(review.recalledAt).format('DD/MM/YYYY HH:mm') : '--'}
                                </span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div>
                                <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>By: </span>
                                <span style={{ color: '#fff' }}>{review.reviewedByName || '--'}</span>
                              </div>
                              <div>
                                <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Date: </span>
                                <span style={{ color: '#fff' }}>
                                  {review.reviewedAt ? moment(review.reviewedAt).format('DD/MM/YYYY HH:mm') : '--'}
                                </span>
                              </div>
                              {review.reviewedByEmail && (
                                <div>
                                  <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Email: </span>
                                  <span style={{ color: '#fff' }}>{review.reviewedByEmail}</span>
                                </div>
                              )}
                              <div>
                                <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>Via: </span>
                                <span style={{ color: '#fff' }}>
                                  {review.reviewedVia === 'external-link' ? 'External link' : 'In-app'}
                                </span>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Rejection reason */}
                        {review.status === 'rejected' && review.rejectionReason && (
                          <div style={{
                            marginTop: '0.5rem',
                            padding: '0.5rem 0.75rem',
                            backgroundColor: 'rgba(255, 80, 80, 0.1)',
                            borderRadius: '4px',
                          }}>
                            <span style={{ color: 'rgba(255, 80, 80, 0.7)', fontSize: '0.8rem', fontWeight: '500' }}>Reason: </span>
                            <span style={{ color: '#fff', fontSize: '0.85rem' }}>{review.rejectionReason}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'flex-end',
          padding: '1rem 1.5rem',
          gap: '0.75rem',
        }}
      >
        {onShareForReview && (
          <Button className="btn-primary-custom" onClick={onShareForReview}>
            <span style={{ position: 'relative', zIndex: 2 }}>Share for Review</span>
          </Button>
        )}
        <Button className="btn-info-custom" onClick={onClose}>
          <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReviewHistoryModal;
